/**
 * Sistema de Permissoes RBAC - PedeJa (Frontend Mirror)
 * Espelho do backend-pedeja/lib/permissions.js
 * MANTER SINCRONIZADO com o backend!
 */

// ==============================
// ROLES VALIDAS DO SISTEMA
// ==============================
export const VALID_ROLES = ['Admin', 'owner', 'manager', 'caixa', 'cozinha', 'entregador', 'garcom'];

// ==============================
// HIERARQUIA DE ROLES
// ==============================
export const ROLE_HIERARCHY = {
    'Admin': 100,
    'owner': 99,
    'empresa': 99, // Backward compat
    'manager': 80,
    'caixa': 50,
    'cozinha': 50,
    'entregador': 40,
    'garcom': 30,
};

// ==============================
// LABELS PARA EXIBICAO
// ==============================
export const ROLE_LABELS = {
    'Admin': { pt: 'Administrador', en: 'Administrator', color: '#e53e3e' },
    'owner': { pt: 'Proprietario', en: 'Owner', color: '#805ad5' },
    'empresa': { pt: 'Proprietario', en: 'Owner', color: '#805ad5' }, // Backward compat
    'manager': { pt: 'Gerente', en: 'Manager', color: '#3182ce' },
    'caixa': { pt: 'Caixa', en: 'Cashier', color: '#38a169' },
    'cozinha': { pt: 'Cozinha', en: 'Kitchen', color: '#d69e2e' },
    'entregador': { pt: 'Entregador', en: 'Delivery', color: '#dd6b20' },
    'garcom': { pt: 'Garcom', en: 'Waiter', color: '#718096' },
};

// ==============================
// PERMISSOES POR ROLE
// ==============================
export const ROLE_PERMISSIONS = {
    'Admin': ['*'],
    'owner': ['*'],
    'empresa': ['*'], // Backward compat

    'manager': [
        'pedidos:read', 'pedidos:write', 'pedidos:advance',
        'catalogo:read', 'catalogo:write',
        'caixa:open', 'caixa:close', 'caixa:view',
        'empresa:config',
        'whatsapp:config',
        'users:invite', 'users:manage',
        'relatorios:view',
        'impressao:manage',
        'estoque:read', 'estoque:write',
        'clientes:read', 'clientes:write',
        'entregadores:manage',
        'mesas:manage',
    ],

    'caixa': [
        'pedidos:read', 'pedidos:write', 'pedidos:advance',
        'catalogo:read',
        'caixa:open', 'caixa:close', 'caixa:view',
        'clientes:read',
        'impressao:manage',
    ],

    'cozinha': [
        'pedidos:read', 'pedidos:advance',
        'catalogo:read',
    ],

    'entregador': [
        'pedidos:read', 'pedidos:advance',
    ],

    'garcom': [
        'mesas:manage',
        'pedidos:read', 'pedidos:write',
    ],
};

// ==============================
// FUNCOES HELPER
// ==============================

/**
 * Verifica se uma role tem determinada permissao
 */
export function hasPermission(role, permission) {
    const permissions = ROLE_PERMISSIONS[role];
    if (!permissions) return false;
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
}

/**
 * Retorna array de permissoes para uma role
 */
export function getPermissionsForRole(role) {
    return ROLE_PERMISSIONS[role] || [];
}

/**
 * Verifica se roleA pode gerenciar roleB
 */
export function canManageRole(managerRole, targetRole) {
    const managerLevel = ROLE_HIERARCHY[managerRole] || 0;
    const targetLevel = ROLE_HIERARCHY[targetRole] || 0;
    return managerLevel > targetLevel;
}

/**
 * Retorna roles que um usuario pode convidar/criar
 */
export function getInvitableRoles(role) {
    const level = ROLE_HIERARCHY[role] || 0;
    return VALID_ROLES.filter(r => {
        const rLevel = ROLE_HIERARCHY[r] || 0;
        return rLevel < level && r !== 'Admin' && r !== 'owner';
    });
}

// ==============================
// SISTEMA MODULAR DE PERMISSOES
// ==============================
// Cada modulo corresponde a um item do sidebar.
// Cada modulo tem 3 niveis: read (visualizar), write (criar/editar), delete (excluir).
// Owner/Admin/empresa: bypass total (nao precisam de modules).

export const VALID_MODULES = [
    'pedidos', 'whatsapp', 'pdv', 'caixa', 'estoque', 'entregadores',
    'informacoes_loja', 'cardapio', 'relatorios', 'salao', 'equipe',
    'minha_conta', 'configuracoes'
];

// Presets sugeridos ao selecionar cargo no convite.
// Owner pode personalizar checkbox por checkbox apos selecionar.
export const CARGO_MODULE_PRESETS = {
    'manager': VALID_MODULES.map(m => ({ module: m, read: true, write: true, delete: true })),
    'caixa': [
        { module: 'pedidos', read: true, write: true, delete: false },
        { module: 'pdv', read: true, write: true, delete: false },
        { module: 'caixa', read: true, write: true, delete: false },
        { module: 'cardapio', read: true, write: false, delete: false },
        { module: 'estoque', read: true, write: false, delete: false },
    ],
    'cozinha': [
        { module: 'pedidos', read: true, write: false, delete: false },
    ],
    'entregador': [
        { module: 'pedidos', read: true, write: false, delete: false },
    ],
    'garcom': [
        { module: 'pedidos', read: true, write: true, delete: false },
        { module: 'salao', read: true, write: true, delete: false },
    ],
};

/**
 * Verifica se usuario tem acesso a um modulo em determinado nivel.
 * Owner/Admin/empresa: bypass total (retorna true sempre).
 * Hierarquia de niveis: delete > write > read (ter delete implica ter write e read).
 *
 * @param {object} user - Objeto do usuario (com role e modules)
 * @param {string} moduleName - Nome do modulo (ex: 'caixa', 'cardapio')
 * @param {string} level - Nivel requerido: 'read', 'write' ou 'delete'
 * @returns {boolean}
 */
export function hasModuleAccess(user, moduleName, level = 'read') {
    // Bypass total para owner/Admin/empresa
    if (['Admin', 'owner', 'empresa'].includes(user.role)) return true;

    const modules = user.modules || [];
    const mod = modules.find(m => m.module === moduleName);
    if (!mod) return false;

    // Hierarquia: delete implica write implica read
    if (level === 'read') return mod.read || mod.write || mod.delete;
    if (level === 'write') return mod.write || mod.delete;
    if (level === 'delete') return mod.delete;
    return false;
}
